import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Star, ArrowLeft, ArrowRight, Quote, TrendingUp } from "lucide-react";
import { TESTIMONIALS } from "../../data/site";

export default function Testimonials() {
  const [i, setI] = useState(0);
  const t = TESTIMONIALS[i];
  const next = () => setI((p) => (p + 1) % TESTIMONIALS.length);
  const prev = () => setI((p) => (p - 1 + TESTIMONIALS.length) % TESTIMONIALS.length);

  return (
    <section
      id="testimonials"
      className="py-20 md:py-28 bg-white"
      data-testid="testimonials-section"
    >
      <div className="container">
        {/* Header row */}
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
          <div className="lg:col-span-8">
            <p className="text-overline text-electric mb-3">07 — Voices</p>
            <h2 className="font-display text-4xl md:text-5xl font-extrabold text-charcoal text-balance">
              What App Founders Say About AppFillip
            </h2>
          </div>
          <div className="lg:col-span-4 lg:justify-self-end flex items-center gap-2">
            <button
              onClick={prev}
              aria-label="Previous testimonial"
              data-testid="testimonial-prev"
              className="w-11 h-11 grid place-items-center border border-charcoal hover:bg-charcoal hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <button
              onClick={next}
              aria-label="Next testimonial"
              data-testid="testimonial-next"
              className="w-11 h-11 grid place-items-center bg-charcoal text-white hover:bg-electric transition-colors"
            >
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 items-stretch">

          {/* ── Main quote card ─────────────────────────────────── */}
          <div className="lg:col-span-7 relative bg-zinc-50 border border-zinc-200 p-8 md:p-12 min-h-[340px] flex flex-col">
            <Quote className="absolute top-6 right-6 w-12 h-12 text-electric/20" aria-hidden />

            <AnimatePresence mode="wait">
              <motion.div
                key={t.name}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.35 }}
                className="flex flex-col flex-1"
              >
                {/* Stars */}
                <div className="flex items-center gap-1 text-electric mb-6" aria-label={`Rated ${t.rating} out of 5`}>
                  {Array.from({ length: t.rating }).map((_, k) => (
                    <Star key={k} className="w-4 h-4 fill-electric" />
                  ))}
                </div>

                {/* Quote */}
                <p className="font-display text-2xl md:text-3xl text-charcoal leading-snug font-medium flex-1">
                  "{t.text}"
                </p>

                {/* Case study metric chip */}
                {t.caseStudy && (
                  <div className="mt-6 inline-flex items-center gap-2 bg-electric/10 border border-electric/20 px-3 py-1.5 w-fit">
                    <TrendingUp className="w-3.5 h-3.5 text-electric flex-shrink-0" />
                    <span className="text-electric font-black text-sm">{t.caseStudy.metric}</span>
                    <span className="text-zinc-500 text-xs">{t.caseStudy.metricLabel}</span>
                  </div>
                )}

                {/* Avatar + name + case study link */}
                <div className="mt-6 flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-4">
                    <img
                      src={t.avatar}
                      alt={`${t.name}, ${t.role}`}
                      className="w-12 h-12 object-cover border border-zinc-200 rounded-sm"
                      width="48"
                      height="48"
                      loading="lazy"
                    />
                    <div>
                      <div className="font-semibold text-charcoal">{t.name}</div>
                      <div className="text-sm text-zinc-500">{t.role}</div>
                    </div>
                  </div>

                  {/* Link to the actual case study */}
                  {t.caseStudy && (
                    <Link
                      to={`/case-studies/${t.caseStudy.slug}`}
                      className="inline-flex items-center gap-1.5 text-xs font-bold text-electric hover:text-charcoal transition-colors border-b border-electric/40 hover:border-charcoal pb-0.5"
                    >
                      See full case study <ArrowRight className="w-3 h-3" />
                    </Link>
                  )}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* ── Thumbnail list ──────────────────────────────────── */}
          <div className="lg:col-span-5 grid gap-3 content-start">
            {TESTIMONIALS.map((tt, idx) => (
              <button
                key={tt.name}
                onClick={() => setI(idx)}
                data-testid={`testimonial-thumb-${idx}`}
                className={`text-left p-4 border transition-all ${
                  idx === i
                    ? "border-charcoal bg-charcoal text-white"
                    : "border-zinc-200 bg-white hover:border-charcoal"
                }`}
              >
                <div className="flex items-center gap-3">
                  <img
                    src={tt.avatar}
                    alt={tt.name}
                    className="w-10 h-10 object-cover rounded-sm flex-shrink-0"
                    width="40"
                    height="40"
                    loading="lazy"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm truncate">{tt.name}</div>
                    <div className={`text-xs truncate ${idx === i ? "text-zinc-300" : "text-zinc-500"}`}>
                      {tt.role}
                    </div>
                  </div>

                  {/* Mini metric badge on thumb */}
                  {tt.caseStudy && (
                    <span className={`text-[10px] font-black px-1.5 py-0.5 flex-shrink-0 ${
                      idx === i ? "bg-electric text-white" : "bg-electric/10 text-electric"
                    }`}>
                      {tt.caseStudy.metric}
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>

        </div>
      </div>
    </section>
  );
}
